import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { RotateCcw, MapPin, Calendar, Percent, Star, Truck, ArrowRight, Loader2 } from "lucide-react";

const CAPACITY_LABELS = {
  PLATEAU: "Plateau",
  DOUBLE_ETAGE: "Double étage",
  PORTE_VOITURES: "Porte-voitures ouvert",
  PORTE_VOITURES_FERME: "Porte-voitures fermé",
};

function fmtDay(iso) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } catch { return iso; }
}

// Lien vers la création de demande, pré-remplie sur l'axe et la fenêtre du trajet
function prefillLink(t) {
  const today = new Date().toISOString().slice(0, 10);
  const date = t.window_start >= today ? t.window_start : today;
  const q = new URLSearchParams({
    pickup_city: t.origin.city, pickup_lat: t.origin.lat, pickup_lng: t.origin.lng,
    pickup_country: t.origin.country || "FR",
    dropoff_city: t.destination.city, dropoff_lat: t.destination.lat, dropoff_lng: t.destination.lng,
    dropoff_country: t.destination.country || "FR",
    date,
  });
  return `/requests/new?${q.toString()}`;
}

export default function TrajetsDisponibles() {
  const [trips, setTrips] = useState(null); // null = chargement

  useEffect(() => {
    let alive = true;
    api.get("/return-trips")
      .then(({ data }) => { if (alive) setTrips(data); })
      .catch(() => { if (alive) setTrips([]); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-4xl">
          <div className="flex items-center gap-2 mb-1">
            <RotateCcw className="w-5 h-5 text-orange-600" />
            <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Zéro trajet à vide</div>
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight mb-1">Trajets retour disponibles</h1>
          <p className="text-sm text-slate-500 mb-6">
            Des dépanneurs repartent à vide sur ces axes et proposent une remise pour remplir leur camion.
            Publiez votre demande sur l'un de ces trajets : elle leur sera présentée en priorité.
          </p>

          {trips === null ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-16 justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-orange-600" /> Chargement…
            </div>
          ) : trips.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
              <RotateCcw className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="font-display font-bold text-lg text-slate-900">Aucun trajet publié pour le moment</div>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                Revenez bientôt, ou publiez votre demande : les dépanneurs dont un retour matche seront alertés automatiquement.
              </p>
              <Link
                to="/requests/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-display font-bold text-sm transition-colors"
              >
                Publier une demande <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="trajets-dispo-list">
              {trips.map((t) => (
                <div key={t.trip_id} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-lg text-slate-900 flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-orange-600 flex-shrink-0" />
                        {t.origin.city} <span className="text-slate-300">→</span> {t.destination.city}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" /> du {fmtDay(t.window_start)} au {fmtDay(t.window_end)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Truck className="w-3.5 h-3.5" /> {CAPACITY_LABELS[t.capacity_type] || t.capacity_type}
                        </span>
                        <span className="font-semibold text-slate-700">
                          {t.places_left}/{t.places} place{t.places > 1 ? "s" : ""} dispo
                        </span>
                        {t.transporter_name && (
                          <span className="flex items-center gap-1">
                            {t.transporter_company || t.transporter_name}
                            {t.transporter_rating_count > 0 && (
                              <span className="flex items-center gap-0.5 text-amber-600">
                                <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                                {Number(t.transporter_rating).toFixed(1)}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {t.discount_pct > 0 && (
                        <span className="text-sm font-display font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full flex items-center gap-0.5">
                          <Percent className="w-3.5 h-3.5" /> −{t.discount_pct} %
                        </span>
                      )}
                      <Link
                        to={prefillLink(t)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-display font-bold text-xs transition-colors"
                      >
                        En profiter <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}